import { FormattedMessage } from '@futrli/localisation';
import {
  getWorkspaceById,
  INTERCOM_LAUNCHER_ID,
  isPending,
  ProductSlug,
  workspaceByIdSelector
} from '@futrli/shared-services';
import { UIFeatherIcon } from '@futrli/ui-components';
import classNames from 'classnames';
import { isNull } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { StoreState } from '../core/redux-utils/types';
import { getCurrentUserId } from '../utils/user';

import styles from './notifications.scss';
import { FlashItem } from './flash-item';

interface Props {
  productSlug: ProductSlug;
  workspaceId: string;
}

/**
 * Functional component to display when the subscription payment has failed.
 * @param {Props} props
 * @returns {UIFlashItem | null}
 */
export const SubscriptionPaymentFailedBanner: React.FC<Props> = ({ workspaceId }: Props) => {
  const [isWorkspaceOwner, setIsWorkspaceOwner] = useState<boolean | null>(null);
  const dispatch = useDispatch();
  const workspace = useSelector(({ normalised }: StoreState) => workspaceByIdSelector(normalised, workspaceId));
  const isWorkspacePending = useSelector((state: StoreState) =>
    isPending(state.loadingRequests.FETCH_FROM_REMOTE_GET_WORKSPACE_BY_ID)
  );

  useEffect(() => {
    if (isWorkspacePending) {
      return;
    }

    if (!workspace) {
      dispatch(getWorkspaceById(workspaceId));
      return;
    }

    const loadData = async () => {
      const userId = await getCurrentUserId();

      setIsWorkspaceOwner(workspace.owner.id === userId);
    };
    loadData();
  }, [workspace, isWorkspacePending, workspaceId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isWorkspacePending || isNull(isWorkspaceOwner)) {
    return null;
  }

  const item = {
    id: 'subcription-payment-failed',
    title: (
      <React.Fragment>
        <span className={classNames(styles.title, 'uiTypeNotificationBody')}>
          <span className="is-hidden-touch">
            <UIFeatherIcon customType="white" iconName="zap" />
          </span>
          &nbsp;
          {isWorkspaceOwner ? (
            <FormattedMessage
              id="notification.payment_issue_message"
              defaultMessage="There is an issue with your payment. Please {CONTACT_SUPPORT} team to update your payment details."
              values={{
                CONTACT_SUPPORT: (
                  <a id={INTERCOM_LAUNCHER_ID}>
                    <FormattedMessage id="common.contact_our_support" defaultMessage="Contact our Support" />
                  </a>
                )
              }}
            />
          ) : (
            <FormattedMessage
              id="notification.non_workspace_owner_payment_issue_message"
              defaultMessage="There is an issue with payment for this organization. Please {CONTACT_SUPPORT} team if you need help."
              values={{
                CONTACT_SUPPORT: (
                  <a id={INTERCOM_LAUNCHER_ID}>
                    <FormattedMessage id="common.contact_our_support" defaultMessage="Contact our Support" />
                  </a>
                )
              }}
            />
          )}
        </span>
      </React.Fragment>
    )
  };

  return <FlashItem item={item} />;
};
